import { get } from "lodash";
import { axiosUCS, axiosUAS } from "../../configs/http-config";
import { setOrigin } from "../../utils/widgetStorage";

/**
 * Fetches widget config and assistant, updates context and storage.
 * @param {string} botId
 * @param {string} tenantId
 * @param {string} lang
 * @param {string} assistantId
 * @param {string} UUID
 * @param {MessageEvent} event - verifyOrigin event (event.origin, event.data.href)
 * @param {Object} callbacks
 * @param {function(string)} callbacks.setPathName
 * @param {function(string)} callbacks.setHostName
 * @param {function(*)} callbacks.setAssistant
 * @param {function(*)} callbacks.setWidget
 * @param {function(boolean)} callbacks.setLoading
 * @param {function(Array)} callbacks.onDomainsLoaded - called with widgetData.domains when loaded
 */
export async function fetchWidget(botId, tenantId, lang, assistantId, UUID, event, callbacks) {
	const { setPathName, setHostName, setAssistant, setWidget, setLoading, onDomainsLoaded } = callbacks;

	try {
		const origin = event.origin;
		const hrefPath = event.data.href ? new URL(event.data.href).pathname : "";

		setPathName(hrefPath);
		setHostName(origin);

		const headers = {
			"X-Host": origin,
			"X-Pathname": hrefPath
		};

		setOrigin({ hostname: origin, pathname: hrefPath });

		const response = await axiosUCS.get(`/widgets/wws/${botId}`, {
			params: { tenantId, lang },
			headers
		});

		let widgetData = null;
		if (response.status === 200) {
			widgetData = get(response, "data.data", null);
			if (widgetData) {
				setWidget(widgetData);
				if (Array.isArray(widgetData.domains) && onDomainsLoaded) {
					onDomainsLoaded(widgetData.domains);
				}
			} else {
				console.warn("No widget data found in response.");
			}
		} else {
			console.error(`Failed to fetch widget. Status code: ${response.status}`);
		}

		// When the bot is connected to a swarm, the embed-script `assistantId`
		// is the swarm `_id`. UCS resolves the primary member and returns
		// `primaryAssistantId` so we fetch the real assistant config here.
		const effectiveAssistantId = widgetData?.primaryAssistantId || assistantId;
		const { data: responseAssistant } = await axiosUAS.get(`/assistants/messenger/${effectiveAssistantId}`, {
			params: { tenantId, uuid: UUID, botId },
			headers
		});
		setAssistant(responseAssistant.data);
	} catch (error) {
		console.error("Error fetching widget:", error);
	} finally {
		setLoading(false);
	}
}
